<template>
  <b-card no-body>
    <b-card-body>
      <!-- Stepper -->
      <div class="wizard-stepper mb-4">
        <div class="wizard-stepper__track">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="wizard-stepper__step"
            :class="{
              'wizard-stepper__step--active': currentStep === index + 1,
              'wizard-stepper__step--completed': currentStep > index + 1,
            }"
          >
            <div class="wizard-stepper__circle">
              <i v-if="currentStep > index + 1" class="bi bi-check-lg"></i>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <span class="wizard-stepper__label">{{ step }}</span>
          </div>
        </div>
      </div>

      <!-- Passo 1: Escolher Operação -->
      <div v-if="currentStep === 1">
        <h5 class="text-center mb-2">Qual operação deseja realizar?</h5>
        <p class="text-center text-muted mb-4">
          Selecione o tipo de operação antes de enviar a planilha.
        </p>

        <b-row class="justify-content-center">
          <b-col md="5" sm="12" class="mb-3">
            <div
              class="wizard-option-card"
              :class="{ 'wizard-option-card--selected': operacao === 'migrar' }"
              @click="operacao = 'migrar'"
            >
              <i class="bi bi-cloud-upload wizard-option-card__icon"></i>
              <h6 class="mb-1">Migrar Dados</h6>
              <small class="text-muted">
                Importar novos itens patrimoniais a partir de uma planilha Excel.
                Cria registros de pessoas, fornecedores, produtos e itens.
              </small>
            </div>
          </b-col>
          <b-col md="5" sm="12" class="mb-3">
            <div
              class="wizard-option-card"
              :class="{ 'wizard-option-card--selected': operacao === 'movimentar' }"
              @click="operacao = 'movimentar'"
            >
              <i class="bi bi-arrow-left-right wizard-option-card__icon"></i>
              <h6 class="mb-1">Movimentar</h6>
              <small class="text-muted">
                Movimentar itens patrimoniais existentes entre departamentos
                a partir de uma planilha Excel.
              </small>
            </div>
          </b-col>
        </b-row>

        <div class="text-center mt-3">
          <b-button variant="primary" :disabled="!operacao" @click="currentStep = 2">
            Próximo
            <i class="bi bi-arrow-right ml-1"></i>
          </b-button>
        </div>
      </div>

      <!-- Passo 2: Upload da Planilha -->
      <div v-if="currentStep === 2">
        <h5 class="text-center mb-2">
          {{ operacao === 'migrar' ? 'Enviar planilha para migração' : 'Enviar planilha para movimentação' }}
        </h5>
        <p class="text-center text-muted mb-4">
          Selecione ou arraste o arquivo Excel com os dados patrimoniais.
        </p>

        <!-- Drop zone -->
        <div
          v-if="!arquivo"
          class="wizard-dropzone"
          :class="{ 'wizard-dropzone--active': dragOver }"
          @click="$refs.fileInput.click()"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="handleDrop"
        >
          <i class="bi bi-file-earmark-spreadsheet wizard-dropzone__icon"></i>
          <p class="mb-1"><strong>Arraste a planilha aqui</strong></p>
          <p class="text-muted mb-0">ou clique para selecionar</p>
          <small class="text-muted">Formatos aceitos: .xlsx, .xls</small>
        </div>

        <!-- Arquivo selecionado -->
        <div v-else class="wizard-file-preview">
          <div class="wizard-file-preview__info">
            <i class="bi bi-file-earmark-spreadsheet-fill wizard-file-preview__icon"></i>
            <div>
              <strong>{{ arquivo.name }}</strong>
              <br />
              <small class="text-muted">{{ formatFileSize(arquivo.size) }}</small>
            </div>
          </div>
          <b-button variant="outline-danger" size="sm" @click="removerArquivo">
            <i class="bi bi-x-lg"></i> Remover
          </b-button>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="handleFileSelect"
        />

        <!-- Erro -->
        <b-alert v-if="erro" variant="danger" show dismissible class="mt-3" @dismissed="erro = null">
          <i class="bi bi-exclamation-triangle-fill mr-1"></i>
          <strong>Erro ao processar planilha:</strong>
          <pre class="wizard-error-detail">{{ erro }}</pre>
        </b-alert>

        <!-- Colunas esperadas -->
        <b-card bg-variant="light" class="mt-4">
          <div class="d-flex align-items-center mb-2">
            <i class="bi bi-info-circle text-primary mr-2"></i>
            <strong>Colunas esperadas na planilha</strong>
          </div>
          <p class="text-muted small mb-2">
            A primeira linha da planilha deve conter os seguintes cabeçalhos, nesta ordem:
          </p>
          <div class="wizard-columns-grid">
            <span
              v-for="col in colunasEsperadas"
              :key="col"
              class="wizard-columns-grid__item"
            >
              {{ col }}
            </span>
          </div>
        </b-card>

        <!-- Botões -->
        <div class="d-flex justify-content-between mt-4">
          <b-button variant="outline-secondary" @click="voltarPasso1">
            <i class="bi bi-arrow-left mr-1"></i> Voltar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!arquivo || processando"
            @click="processarArquivo"
          >
            <b-spinner v-if="processando" small class="mr-1"></b-spinner>
            {{ processando ? 'Processando...' : 'Processar Planilha' }}
            <i v-if="!processando" class="bi bi-arrow-right ml-1"></i>
          </b-button>
        </div>
      </div>

      <!-- Passo 3: Revisão -->
      <div v-if="currentStep === 3">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 class="mb-0">Revisão dos Dados</h5>
            <p class="text-muted mb-0">
              {{ operacao === 'migrar' ? 'Migração' : 'Movimentação' }} —
              <strong>{{ records.length }}</strong> registro(s) processado(s) com sucesso.
            </p>
          </div>
          <b-badge variant="success" class="px-3 py-2" style="font-size: 0.9rem">
            <i class="bi bi-check-circle mr-1"></i>
            Concluído
          </b-badge>
        </div>

        <b-card bg-variant="light" no-body>
          <b-table
            responsive
            small
            hover
            show-empty
            :items="paginatedRecords"
            :fields="tableFields"
          >
            <template #empty>
              <p class="text-center text-muted my-3">Nenhum registro encontrado</p>
            </template>
          </b-table>

          <!-- Paginação -->
          <div
            v-if="records.length > 0"
            class="d-flex align-items-center justify-content-between px-3 py-2"
          >
            <small class="text-muted">
              Mostrando {{ paginaInicio }} a {{ paginaFim }} de {{ records.length }} registros
            </small>
            <div class="d-flex align-items-center">
              <b-form-select
                v-model="perPage"
                :options="[10, 25, 50]"
                size="sm"
                class="mr-2"
                style="width: 80px"
              ></b-form-select>
              <b-pagination
                v-model="currentPage"
                :total-rows="records.length"
                :per-page="perPage"
                size="sm"
                class="mb-0"
              ></b-pagination>
            </div>
          </div>
        </b-card>

        <!-- Botões -->
        <div class="d-flex justify-content-between mt-4">
          <b-button variant="outline-secondary" @click="novaImportacao">
            <i class="bi bi-arrow-repeat mr-1"></i> Nova Importação
          </b-button>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import ResourceService from "@/core/api/ResourceService";

export default {
  data() {
    return {
      currentStep: 1,
      operacao: null,
      arquivo: null,
      dragOver: false,
      processando: false,
      records: [],
      erro: null,
      currentPage: 1,
      perPage: 10,
      steps: ["Operação", "Planilha", "Revisão"],
      colunasEsperadas: [
        "orgao", "unidade", "gestao", "uorg", "conta_contabil",
        "numero_patrimonial_antigo", "numero_patrimonial", "data_tombamento",
        "codigo_material", "nome_material", "descricao_complementar",
        "marca", "modelo", "serie", "fabricante", "processo",
        "codigo_terceiros", "valor_inicial_bem", "valor_bem",
        "valor_depreciado", "valor_liquido", "valor_doacao_venda",
        "codigo_tipo_bem", "nome_tipo_bem", "codigo_situacao",
        "nome_situacao", "codigo_destinacao", "nome_destinacao",
        "cpf_responsavel", "nome_responsavel", "cpf_responsavel_uorg",
        "nome_responsavel_uorg",
      ],
    };
  },

  beforeMount() {
    this.$serviceMigrar = new ResourceService("/api/patrimonio-excel/migrar-antt-planilha");
    this.$serviceMovimentar = new ResourceService("/api/patrimonio-excel/movimentar");
  },

  computed: {
    tableFields() {
      return [
        { key: "orgao", label: "Órgão" },
        { key: "unidade", label: "Unidade" },
        { key: "gestao", label: "Gestão" },
        { key: "uorg", label: "UORG" },
        { key: "contaContabil", label: "Conta Contábil" },
        { key: "numeroPatrimonialAntigo", label: "N. Patrimonial Antigo" },
        { key: "numeroPatrimonial", label: "N. Patrimonial" },
        { key: "dataTombamento", label: "Data Tombamento" },
        { key: "codigoMaterial", label: "Cód. Material" },
        { key: "nomeMaterial", label: "Nome Material" },
        { key: "descricaoComplementar", label: "Descrição" },
        { key: "modelo", label: "Modelo" },
        { key: "serie", label: "Série" },
        { key: "fabricante", label: "Fabricante" },
        { key: "processo", label: "Processo" },
        { key: "codigoTerceiros", label: "Cód. Terceiros" },
        { key: "valorInicialBem", label: "Valor Inicial" },
        { key: "valorBem", label: "Valor do Bem" },
        { key: "valorDepreciado", label: "Valor Depreciado" },
        { key: "valorLiquido", label: "Valor Líquido" },
        { key: "valorDoacaoVenda", label: "Valor Doação/Venda" },
        { key: "codigoTipoBem", label: "Cód. Tipo Bem" },
        { key: "nomeTipoBem", label: "Nome Tipo Bem" },
        { key: "codigoSituacao", label: "Cód. Situação" },
        { key: "nomeSituacao", label: "Nome Situação" },
        { key: "codigoDestinacao", label: "Cód. Destinação" },
        { key: "nomeDestinacao", label: "Nome Destinação" },
        { key: "cpfResponsavel", label: "CPF Responsável" },
        { key: "nomeResponsavel", label: "Nome Responsável" },
        { key: "cpfResponsavelUorg", label: "CPF Resp. UORG" },
        { key: "nomeResponsavelUorg", label: "Nome Resp. UORG" },
      ];
    },

    paginatedRecords() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.records.slice(start, start + this.perPage);
    },

    paginaInicio() {
      return Math.min((this.currentPage - 1) * this.perPage + 1, this.records.length);
    },

    paginaFim() {
      return Math.min(this.currentPage * this.perPage, this.records.length);
    },
  },

  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && this.validarArquivo(file)) {
        this.arquivo = file;
        this.erro = null;
      }
      this.$refs.fileInput.value = null;
    },

    handleDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      if (file && this.validarArquivo(file)) {
        this.arquivo = file;
        this.erro = null;
      }
    },

    validarArquivo(file) {
      const extensoesValidas = [".xlsx", ".xls"];
      const extensao = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!extensoesValidas.includes(extensao)) {
        this.$notify({
          type: "danger",
          message: "Formato inválido. Selecione um arquivo .xlsx ou .xls",
        });
        return false;
      }
      return true;
    },

    removerArquivo() {
      this.arquivo = null;
      this.erro = null;
    },

    voltarPasso1() {
      this.currentStep = 1;
      this.arquivo = null;
      this.erro = null;
    },

    novaImportacao() {
      this.currentStep = 1;
      this.operacao = null;
      this.arquivo = null;
      this.records = [];
      this.erro = null;
      this.currentPage = 1;
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / 1048576).toFixed(1) + " MB";
    },

    async processarArquivo() {
      if (!this.arquivo) return;

      this.processando = true;
      this.erro = null;

      const formData = new FormData();
      formData.append("arquivo", this.arquivo);

      const service =
        this.operacao === "migrar" ? this.$serviceMigrar : this.$serviceMovimentar;

      try {
        const { data } = await service.saveFormData(null, formData);
        this.records = data;
        this.currentPage = 1;
        this.currentStep = 3;
        this.$notify({
          type: "success",
          message: `${data.length} registro(s) processado(s) com sucesso!`,
        });
      } catch (error) {
        const msg =
          error.response && error.response.data
            ? error.response.data
            : "Erro desconhecido ao processar a planilha.";
        this.erro = msg;
        this.$notify({ type: "danger", message: "Erro ao processar a planilha" });
      } finally {
        this.processando = false;
      }
    },
  },
};
</script>

<style scoped>
/* Stepper */
.wizard-stepper {
  padding: 0 20px;
}

.wizard-stepper__track {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0;
}

.wizard-stepper__step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  position: relative;
}

.wizard-stepper__step:not(:last-child)::after {
  content: "";
  display: block;
  width: 40px;
  height: 2px;
  background: #dee2e6;
  margin-left: 8px;
}

.wizard-stepper__step--completed:not(:last-child)::after {
  background: #2ebd85;
}

.wizard-stepper__step--active:not(:last-child)::after {
  background: #dee2e6;
}

.wizard-stepper__circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  background: #e9ecef;
  color: #6c757d;
  flex-shrink: 0;
}

.wizard-stepper__step--active .wizard-stepper__circle {
  background: #24528d;
  color: #fff;
}

.wizard-stepper__step--completed .wizard-stepper__circle {
  background: #2ebd85;
  color: #fff;
}

.wizard-stepper__label {
  font-size: 13px;
  color: #6c757d;
  white-space: nowrap;
}

.wizard-stepper__step--active .wizard-stepper__label {
  color: #24528d;
  font-weight: 600;
}

.wizard-stepper__step--completed .wizard-stepper__label {
  color: #2ebd85;
}

/* Cards de operação */
.wizard-option-card {
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.wizard-option-card:hover {
  border-color: #24528d;
  box-shadow: 0 2px 8px rgba(36, 82, 141, 0.12);
}

.wizard-option-card--selected {
  border-color: #24528d;
  background: #f0f5fb;
  box-shadow: 0 2px 12px rgba(36, 82, 141, 0.15);
}

.wizard-option-card__icon {
  font-size: 36px;
  color: #24528d;
  margin-bottom: 12px;
  display: block;
}

/* Dropzone */
.wizard-dropzone {
  border: 2px dashed #ced4da;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafbfc;
}

.wizard-dropzone:hover {
  border-color: #24528d;
  background: #f0f5fb;
}

.wizard-dropzone--active {
  border-color: #24528d;
  background: #e8f0fe;
  box-shadow: 0 0 0 3px rgba(36, 82, 141, 0.1);
}

.wizard-dropzone__icon {
  font-size: 48px;
  color: #6c757d;
  margin-bottom: 12px;
  display: block;
}

.wizard-dropzone--active .wizard-dropzone__icon {
  color: #24528d;
}

/* File preview */
.wizard-file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 2px solid #2ebd85;
  border-radius: 12px;
  padding: 16px 20px;
  background: #f0faf5;
}

.wizard-file-preview__info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.wizard-file-preview__icon {
  font-size: 32px;
  color: #2ebd85;
}

/* Error detail */
.wizard-error-detail {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 12px;
  background: #fff5f5;
  padding: 8px 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Columns grid */
.wizard-columns-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.wizard-columns-grid__item {
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-family: monospace;
  color: #495057;
}
</style>
